import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import ChevronRight from '@material-ui/icons/ChevronRight';
import LabResult from '@solvhealth/types/interfaces/LabResult';
import { ResponseType } from '@solvhealth/types/interfaces/dapi';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { colors } from '../../../../../constants/colors';
import useToggle from '../../../../../hooks/useToggle';
import Loading from '../../../../SolvPatternLibrary/Loading';
import { apiGetJson } from '../../../../../core/dapi';
import { getLabResultsByBookingUrl } from '../../../../../core/dapi/labResults';
import useRuntimeVariable from '../../../../../hooks/runtime/useRuntimeVariable';
import history from '../../../../../core/history';
import DateTime, { MONTH_DAY_YEAR } from '../../../../DateTime';
import CloseX from '../../../../SolvPatternLibrary/Icons/CloseX';

const Root = styled.div.withConfig({ componentId: 'sc-6vys6m-0' })`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  font-size: ${fontSize.default};
  font-family: ${fontFamily.book};
  color: ${colors.squid};
`;

const BlueError = styled.div.withConfig({ componentId: 'sc-6vys6m-1' })`
  padding: 8px;
  font-size: ${fontSize.extraSmall};
  font-family: ${fontFamily.book};
  color: ${colors.lightText};
  background: ${colors.blueCorona};
`;

const DRoot = styled.div.withConfig({ componentId: 'sc-6vys6m-2' })`
  padding: 24px;
`;

const Title = styled.div.withConfig({ componentId: 'sc-6vys6m-3' })`
  font-size: ${fontSize.mediumLarge};
  font-family: ${fontFamily.bold};
  color: ${colors.squid};
`;

const Li = styled.li.withConfig({ componentId: 'sc-6vys6m-4' })`
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  color: ${colors.link};
  cursor: pointer;
  list-style-image: url(/images/icons/list/document.svg);
  padding: 18px 0 14px;

  &:not(&:last-child) {
    border-bottom: 1px solid ${colors.borderLine};
  }
  &:hover {
    text-decoration: underline;
  }
`;

const Flex = styled.span.withConfig({ componentId: 'sc-6vys6m-5' })`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  transform: translateY(-4px);
  width: 100%;
`;

interface Props {
  bookingId: string;
  lastName: string;
}

const ViewResults = ({ bookingId, lastName }: Props) => {
  const [open, toggle] = useToggle();
  const [fetching, setFetching] = useState({ loading: false, hasLoaded: false });
  const [err, setErr] = useState('');
  const [labResults, setLabResults] = useState<LabResult[]>([]);
  const [, setLastName] = useRuntimeVariable('otpLoginLastName');
  useEffect(() => {
    if (open) {
      // set booking name so user doesn't have to double verify
      setLastName(lastName);
      setFetching({ loading: true, hasLoaded: false });

      apiGetJson<LabResult, ResponseType.Paginated>(
        getLabResultsByBookingUrl(bookingId, lastName, false)
      )
        .then((resp) => setLabResults(resp.data.results))
        .catch((err) => setErr(JSON.stringify(err)))
        .finally(() => {
          setFetching({
            loading: false,
            hasLoaded: true,
          });
        });
    }
  }, [open, setLabResults, bookingId, lastName, setLastName]);

  return (
    <>
      <Root onClick={toggle}>
        <span>View test results</span>
        <ChevronRight />
      </Root>
      <Dialog fullWidth onClose={toggle} open={open}>
        <DRoot>
          <CloseX onClick={toggle} />
          <Title>Select lab result:</Title>
          {fetching.loading && <Loading />}
          {fetching.hasLoaded && err && (
            <BlueError>
              Sorry, something went wrong while retrieving lab results associated with this booking
              :(
              <br />
              {err}
            </BlueError>
          )}
          <ul>
            {labResults?.map((result) => (
              <Li key={result.id} onClick={() => history.push(`/lab-results/${result.id}`)}>
                <Flex>
                  <span>
                    "{result.test_type}" test uploaded on{' '}
                    <DateTime
                      dateTime={result.created_date}
                      format={MONTH_DAY_YEAR}
                      Wrapper="span"
                    />
                  </span>
                  <ChevronRight />
                </Flex>
              </Li>
            ))}
          </ul>
        </DRoot>
      </Dialog>
    </>
  );
};

export default ViewResults;
